import type { SignInLang } from '../../lib/signInCopy'
import { COLLECT_COPY } from '../../lib/collectCopy'
import type { HandoverView } from '../../lib/shop'

/**
 * The body of #/collect — every state the scanned page can be in. REC-49.
 * Split from routes/Collect.tsx only so the suite can render it: the route
 * imports the logo SVG, which the test bundle cannot load. The rules for this
 * page are in the route's header.
 */
export type CollectPhase =
  | { kind: 'loading' }
  | { kind: 'view'; view: HandoverView; justCollected: boolean }
  | { kind: 'error' }

/** Pure, so the suite can render every state without a database. */
export function CollectBody({
  phase,
  lang,
  busy = false,
  onConfirm = () => {},
  onRetry = () => {},
}: {
  phase: CollectPhase
  lang: SignInLang
  busy?: boolean
  onConfirm?: () => void
  onRetry?: () => void
}) {
  const t = COLLECT_COPY[lang]

  if (phase.kind === 'loading') {
    return <p className="signin__lede" aria-busy="true">{t.checking}</p>
  }
  if (phase.kind === 'error') {
    return (
      <div className="collect__state" role="alert">
        <h1 className="signin__title">{t.errorTitle}</h1>
        <p className="signin__lede">{t.errorBody}</p>
        <button type="button" className="btn btn--quiet" onClick={onRetry}>{t.retry}</button>
      </div>
    )
  }

  const v = phase.view
  const item = v.itemName && (
    <div className="collect__item">
      <p className="collect__itemname">{v.itemName}</p>
      {v.variantLabel && v.variantLabel !== 'Standard' && (
        <p className="collect__variant">{v.variantLabel}</p>
      )}
      {v.forWhom && <p className="collect__who">{t.forWhom(v.forWhom)}</p>}
      {v.roomName && <p className="collect__room">{t.atRoom(v.roomName)}</p>}
    </div>
  )

  switch (v.state) {
    case 'ready':
      return (
        <div className="collect__state">
          <h1 className="signin__title">{t.readyTitle}</h1>
          {item}
          <p className="signin__lede">{t.readyCheck}</p>
          <button
            type="button"
            className="btn btn--primary collect__confirm"
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? t.confirming : t.confirm}
          </button>
          <p className="signin__help">{t.noSignIn}</p>
        </div>
      )
    case 'collected':
      return (
        <div className="collect__state collect__state--done" role="status">
          <h1 className="signin__title">
            <span className="collect__tick" aria-hidden="true">✓ </span>
            {phase.justCollected ? t.doneTitle : t.alreadyTitle}
          </h1>
          {item}
          <p className="signin__lede">{phase.justCollected ? t.doneBody : t.alreadyBody}</p>
        </div>
      )
    case 'expired':
      return (
        <div className="collect__state">
          <h1 className="signin__title">{t.expiredTitle}</h1>
          <p className="signin__lede">{t.expiredBody}</p>
        </div>
      )
    case 'not_ready':
      return (
        <div className="collect__state">
          <h1 className="signin__title">{t.notReadyTitle}</h1>
          {item}
          <p className="signin__lede">{t.notReadyBody}</p>
        </div>
      )
    default:
      return (
        <div className="collect__state">
          <h1 className="signin__title">{t.unknownTitle}</h1>
          <p className="signin__lede">{t.unknownBody}</p>
        </div>
      )
  }
}
